import type {
  Visitor,
  Lead,
  DashboardStats,
  ChartDataPoint,
  DeviceBreakdown,
  ApiResponse,
  TopInteraction,
  WorkCard,
  BrandPerformance,
  SocialDataPoint,
} from '@/types';
import { detectPlatform } from '@/lib/platform-detector';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://hashtaginc.co.in/api';

// Session-token auth — the token is issued by /api/auth/login and kept in
// localStorage. Nothing credential-related is baked into the bundle.
const TOKEN_STORAGE = 'lf_session';
const USER_STORAGE = 'lf_user';
export const getSessionToken = () => localStorage.getItem(TOKEN_STORAGE) || '';
export const setSessionToken = (t: string) => localStorage.setItem(TOKEN_STORAGE, t);
export const setSessionUser = (u: { email: string; name?: string }) =>
  localStorage.setItem(USER_STORAGE, JSON.stringify(u));
export const getSessionUserEmail = (): string => {
  try {
    return JSON.parse(localStorage.getItem(USER_STORAGE) || '{}').email || '';
  } catch {
    return '';
  }
};
export const clearSession = () => {
  localStorage.removeItem(TOKEN_STORAGE);
  localStorage.removeItem(USER_STORAGE);
};

export interface AuthResult {
  status: 'success' | 'error';
  message?: string;
  token?: string;
  user?: { email: string; name?: string };
}

async function authPost(path: string, body: Record<string, unknown>): Promise<AuthResult> {
  try {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getSessionToken()}`,
      },
      body: JSON.stringify(body),
    });
    return (await res.json()) as AuthResult;
  } catch {
    return { status: 'error', message: 'Network error. Please try again.' };
  }
}

export const auth = {
  login: (email: string, password: string) => authPost('/auth/login', { email, password }),
  register: (email: string, name: string, password: string) =>
    authPost('/auth/register', { email, name, password }),
  verify: (email: string, otp: string) => authPost('/auth/verify', { email, otp }),
  forgot: (email: string) => authPost('/auth/forgot', { email }),
  reset: (email: string, otp: string, password: string) =>
    authPost('/auth/reset', { email, otp, password }),
  logout: () => authPost('/auth/logout', {}),
};

// ── Simple in-memory cache ───────────────────────────────────
const cache = new Map<string, { data: unknown; expiry: number }>();
const CACHE_TTL = 30_000; // 30 seconds

// ── Request deduplication ─────────────────────────────────────
const pendingRequests = new Map<string, Promise<unknown>>();

// ── Error reporting — Layout shows a banner when API calls fail ──
type ApiErrorListener = (message: string) => void;
const errorListeners = new Set<ApiErrorListener>();

export function onApiError(listener: ApiErrorListener): () => void {
  errorListeners.add(listener);
  return () => errorListeners.delete(listener);
}

function reportApiError(message: string) {
  errorListeners.forEach(cb => cb(message));
}

// ── Lead Actions Types ───────────────────────────────────────

export interface LeadAction {
  name:              string | null;
  brand:             string | null;
  email:             string | null;
  phone:             string | null;
  stage:             string | null;
  help:              string | null;
  call_status:       string | null;
  call_connected_at: string | null;
  call_remark:       string | null;
  discovery_call:    string | null;
  proposal:          string | null;
  proposal_date_time: string | null;
  project:           string | null;
  platform:          string | null;
  notes:             string | null;
  assigned_to:       string | null;
  updated_at?:       string;
}

export interface LeadActionsMap {
  [leadId: string]: LeadAction;
}

export type TeamRole = 'member' | 'admin' | 'developer';

export interface TeamMember {
  id: number;
  name: string;
  email: string;
  role?: TeamRole;
  created_at?: string;
}

export interface ManualLead {
  id: number;
  name: string | null;
  brand: string | null;
  email: string | null;
  phone: string | null;
  stage: string | null;
  help: string | null;
  call_status: string | null;
  call_connected_at: string | null;
  call_remark: string | null;
  discovery_call: string | null;
  proposal: string | null;
  proposal_date_time: string | null;
  project: string | null;
  notes: string | null;
  assigned_to: string | null;
  created_at: string;
  updated_at?: string;
}

type EditableField =
  | 'call_status'
  | 'call_connected_at'
  | 'call_remark'
  | 'discovery_call'
  | 'proposal'
  | 'proposal_date_time'
  | 'project'
  | 'platform'
  | 'notes'
  | 'assigned_to';

// ── API Client ───────────────────────────────────────────────

class ApiClient {
  private async fetch<T>(
    endpoint: string,
    options?: RequestInit,
    useCache = false,
  ): Promise<ApiResponse<T>> {
    const cacheKey = `${endpoint}:${JSON.stringify(options)}`;
    
    // Check cache for GET requests
    if (useCache && (!options || options.method === undefined || options.method === 'GET')) {
      const cached = cache.get(cacheKey);
      if (cached && Date.now() < cached.expiry) {
        return cached.data as ApiResponse<T>;
      }
    }
    
    // Deduplicate in-flight requests
    if (pendingRequests.has(cacheKey)) {
      return pendingRequests.get(cacheKey) as Promise<ApiResponse<T>>;
    }
    
    const requestPromise = (async () => {
      try {
        const token = getSessionToken();
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...options?.headers,
          },
        });

        if (response.status === 401) {
          clearSession();
          window.location.reload();
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data && data.status === 'error') {
          reportApiError(data.message || 'API error');
        }

        // Store in cache
        if (useCache && (!options || options.method === undefined || options.method === 'GET')) {
          cache.set(cacheKey, { data, expiry: Date.now() + CACHE_TTL });
        }
        
        return data;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error occurred';
        reportApiError(message);
        return {
          status:  'error',
          message,
        };
      } finally {
        pendingRequests.delete(cacheKey);
      }
    })();
    
    pendingRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }

  // Clear cache for specific endpoint or all cache
  clearCache(endpoint?: string): void {
    if (endpoint) {
      for (const key of cache.keys()) {
        if (key.startsWith(endpoint)) cache.delete(key);
      }
    } else {
      cache.clear();
    }
  }

  // ── Dashboard statistics ─────────────────────────────────────
  async getDashboardStats(days: number = 30): Promise<DashboardStats> {
    const response = await this.fetch<DashboardStats>(`/stats.php?days=${days}`, undefined, true);
    if (response.status === 'success' && response.data) return response.data;
    throw new Error(response.message || 'Failed to fetch stats');
  }

  // ── Visitors ─────────────────────────────────────────────────
  async getVisitors(
    page: number = 1,
    limit: number = 50,
    filters?: {
      startDate?: string;
      endDate?: string;
      device?: string;
      becameLead?: string;
      search?: string;
    },
  ): Promise<{ visitors: Visitor[]; total: number; pages: number }> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters?.startDate)  params.append('startDate',  filters.startDate);
    if (filters?.endDate)    params.append('endDate',    filters.endDate);
    if (filters?.device)     params.append('device',     filters.device);
    if (filters?.becameLead) params.append('becameLead', filters.becameLead);
    if (filters?.search)     params.append('search',     filters.search);

    const response = await this.fetch<{ visitors: Visitor[]; total: number; pages: number }>(
      `/visitors.php?${params.toString()}`,
    );
    if (response.status === 'success' && response.data) return response.data;
    throw new Error(response.message || 'Failed to fetch visitors');
  }

  async getVisitorById(id: string): Promise<Visitor> {
    const response = await this.fetch<Visitor>(`/visitors.php?id=${id}`);
    if (response.status === 'success' && response.data) return response.data;
    throw new Error(response.message || 'Failed to fetch visitor');
  }

  // ── Leads ─────────────────────────────────────────────────────
  async getLeads(
    page: number = 1,
    limit: number = 50,
    filters?: {
      startDate?: string;
      endDate?: string;
      stage?: string;
      search?: string;
    },
  ): Promise<{ leads: Lead[]; total: number; pages: number }> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate)   params.append('endDate',   filters.endDate);
    if (filters?.stage)     params.append('stage',     filters.stage);
    if (filters?.search)    params.append('search',    filters.search);

    const response = await this.fetch<{ leads: Lead[]; total: number; pages: number }>(
      `/leads.php?${params.toString()}`,
    );
    if (response.status === 'success' && response.data) return response.data;
    throw new Error(response.message || 'Failed to fetch leads');
  }

  async getLeadById(id: number): Promise<Lead> {
    const response = await this.fetch<Lead>(`/leads.php?id=${id}`);
    if (response.status === 'success' && response.data) return response.data;
    throw new Error(response.message || 'Failed to fetch lead');
  }

  // ══════════════════════════════════════════════════════════
  //  LEAD ACTIONS
  // ══════════════════════════════════════════════════════════

  /**
   * Sync base lead data (name, brand, email, phone, stage, help)
   * into lead_actions table. Does NOT touch editable columns.
   */
  async syncLeadBaseData(leads: Lead[]): Promise<void> {
    if (leads.length === 0) return;

    const payload = {
      mode:  'sync',
      leads: leads.map(l => ({
        id:       l.id,
        name:     l.name  || null,
        brand:    l.brand || null,
        email:    l.email || null,
        phone:    l.phone || null,
        stage:    l.stage || null,
        help:     l.help  || null,
        platform: detectPlatform(l) || null,
      })),
    };

    try {
      await this.fetch('/lead-actions.php', {
        method: 'POST',
        body:   JSON.stringify(payload),
      });
    } catch (err) {
      console.warn('Lead sync failed:', err);
    }
  }

  /**
   * Fetch persisted actions for given lead IDs.
   */
  async getLeadActions(leadIds: number[]): Promise<LeadActionsMap> {
    if (leadIds.length === 0) return {};
    const ids      = leadIds.join(',');
    const response = await this.fetch<LeadActionsMap>(
      `/lead-actions.php?lead_ids=${ids}`,
    );
    if (response.status === 'success' && response.data) return response.data;
    console.warn('Failed to fetch lead actions:', response.message);
    return {};
  }

  /**
   * Update a single editable field for a lead.
   */
  async updateLeadAction(
    leadId: number,
    field:  EditableField,
    value:  string,
  ): Promise<{ data: LeadAction; emailSent?: boolean; emailError?: string; emailDetails?: string } | null> {
    const body: Record<string, unknown> = {
      lead_id: leadId,
      [field]: value,
    };
    const response = await this.fetch<LeadAction>('/lead-actions.php', {
      method: 'POST',
      body:   JSON.stringify(body),
    });
    if (response.status === 'success' && response.data) {
      return {
        data:        response.data as LeadAction,
        emailSent:   (response as unknown as Record<string, unknown>).emailSent as boolean | undefined,
        emailError:  (response as unknown as Record<string, unknown>).emailError as string | undefined,
        emailDetails:(response as unknown as Record<string, unknown>).emailDetails as string | undefined,
      };
    }
    console.error('Failed to save lead action:', response.message);
    return null;
  }

  // ══════════════════════════════════════════════════════════
  //  MANUAL LEADS — fully separate from leads / lead_actions
  // ══════════════════════════════════════════════════════════

  async getManualLeads(
    page: number = 1,
    limit: number = 50,
    filters?: {
      startDate?: string;
      endDate?: string;
      stage?: string;
      search?: string;
    },
  ): Promise<{ leads: ManualLead[]; total: number; pages: number }> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate)   params.append('endDate',   filters.endDate);
    if (filters?.stage)     params.append('stage',     filters.stage);
    if (filters?.search)    params.append('search',    filters.search);

    const response = await this.fetch<{ leads: ManualLead[]; total: number; pages: number }>(
      `/manual-leads.php?${params.toString()}`,
    );
    if (response.status === 'success' && response.data) return response.data;
    throw new Error(response.message || 'Failed to fetch manual leads');
  }

  async createManualLead(data: {
    name: string;
    brand?: string;
    email?: string;
    phone?: string;
    stage?: string;
    help?: string;
  }): Promise<ManualLead> {
    const response = await this.fetch<ManualLead>('/manual-leads.php', {
      method: 'POST',
      body:   JSON.stringify(data),
    });
    if (response.status === 'success' && response.data) return response.data;
    throw new Error(response.message || 'Failed to create manual lead');
  }

  async updateManualLead(
    manualLeadId: number,
    field: EditableField,
    value: string,
  ): Promise<ManualLead | null> {
    const body: Record<string, unknown> = {
      manual_lead_id: manualLeadId,
      [field]: value,
    };
    const response = await this.fetch<ManualLead>('/manual-leads.php', {
      method: 'POST',
      body:   JSON.stringify(body),
    });
    if (response.status === 'success' && response.data) return response.data;
    console.error('Failed to save manual lead field:', response.message);
    return null;
  }

  // ── Team members ─────────────────────────────────────────────
  async getTeamMembers(): Promise<TeamMember[]> {
    const response = await this.fetch<TeamMember[]>('/team.php');
    if (response.status === 'success' && response.data) return response.data;
    throw new Error(response.message || 'Failed to fetch team members');
  }

  async addTeamMember(name: string, email: string): Promise<TeamMember> {
    const response = await this.fetch<TeamMember>('/team.php', {
      method: 'POST',
      body:   JSON.stringify({ name, email }),
    });
    if (response.status === 'success' && response.data) return response.data;
    throw new Error(response.message || 'Failed to add team member');
  }

  async setTeamMemberRole(id: number, role: TeamRole): Promise<void> {
    const response = await this.fetch('/team.php', {
      method: 'PUT',
      body:   JSON.stringify({ id, role }),
    });
    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed to update role');
    }
  }

  async deleteTeamMember(id: number): Promise<void> {
    const response = await this.fetch(`/team.php?id=${id}`, { method: 'DELETE' });
    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed to remove member');
    }
  }

  // ── Charts ───────────────────────────────────────────────────
  async getVisitorChart(
    startDate?: string,
    endDate?: string,
    days: number = 30,
  ): Promise<ChartDataPoint[]> {
    const params = new URLSearchParams();
    if (startDate && endDate) {
      params.append('startDate', startDate);
      params.append('endDate',   endDate);
    } else {
      params.append('days', String(days));
    }
    const response = await this.fetch<ChartDataPoint[]>(
      `/chart-visitors.php?${params.toString()}`,
      undefined,
      true,
    );
    if (response.status === 'success' && response.data) return response.data;
    throw new Error(response.message || 'Failed to fetch visitor chart');
  }

  async getDeviceBreakdown(
    startDate?: string,
    endDate?: string,
    days: number = 30,
  ): Promise<DeviceBreakdown[]> {
    const params = new URLSearchParams();
    if (startDate && endDate) {
      params.append('startDate', startDate);
      params.append('endDate',   endDate);
    } else {
      params.append('days', String(days));
    }
    const response = await this.fetch<DeviceBreakdown[]>(
      `/device-breakdown.php?${params.toString()}`,
      undefined,
      true,
    );
    if (response.status === 'success' && response.data) return response.data;
    throw new Error(response.message || 'Failed to fetch device breakdown');
  }

  async getTopInteractions(): Promise<TopInteraction[]> {
    const response = await this.fetch<TopInteraction[]>('/top-interactions.php');
    if (response.status === 'success' && response.data) return response.data;
    throw new Error(response.message || 'Failed to fetch top interactions');
  }

  async getWorkCards(): Promise<WorkCard[]> {
    const response = await this.fetch<WorkCard[]>('/work-cards.php');
    if (response.status === 'success' && response.data) return response.data;
    throw new Error(response.message || 'Failed to fetch work cards');
  }

  async getBrandPerformance(): Promise<BrandPerformance[]> {
    const response = await this.fetch<BrandPerformance[]>('/brand-performance.php');
    if (response.status === 'success' && response.data) return response.data;
    throw new Error(response.message || 'Failed to fetch brand performance');
  }

  async getSocialInteractions(days: number = 30): Promise<SocialDataPoint[]> {
    const response = await this.fetch<SocialDataPoint[]>(
      `/social-interactions.php?days=${days}`,
    );
    if (response.status === 'success' && response.data) return response.data;
    throw new Error(response.message || 'Failed to fetch social interactions');
  }

  async getRecentLeads(limit: number = 5): Promise<Lead[]> {
    const response = await this.fetch<Lead[]>(`/leads.php?recent=${limit}`);
    if (response.status === 'success' && response.data) return response.data;
    throw new Error(response.message || 'Failed to fetch recent leads');
  }

  async getAggregates(
    startDate: string,
    endDate: string,
  ): Promise<{
    interactions: TopInteraction[];
    workCards:    WorkCard[];
    brands:       BrandPerformance[];
    socialByDay:  SocialDataPoint[];
    recentLeads:  Lead[];
  }> {
    const visitorsAll: Visitor[] = [];
    let page = 1, pages = 1;
    do {
      const res = await this.getVisitors(page, 100, { startDate, endDate });
      visitorsAll.push(...(res.visitors || []));
      pages = res.pages || 1;
      page++;
    } while (page <= pages);

    const leadsAll: Lead[] = [];
    page = 1; pages = 1;
    do {
      const res = await this.getLeads(page, 100, { startDate, endDate });
      leadsAll.push(...(res.leads || []));
      pages = res.pages || 1;
      page++;
    } while (page <= pages);

    const parseList = (raw: string): string[] => {
      if (!raw) return [];
      const cleaned = raw.trim()
        .replace(/^\[|\]$/g, '')
        .replace(/"/g, '')
        .replace(/\{/g, '')
        .replace(/\}/g, '')
        .replace(/\s+/g, ' ');
      return cleaned.split(',').map(s => s.trim()).filter(Boolean);
    };

    const dateKey = (dt: string): string => dt ? dt.slice(0, 10) : '';

    const interactionCounts: Record<string, number> = {
      'Nav Bar CTA': 0,
      'Hero Plays':  0,
      'Hero Pauses': 0,
      'Hero Mute':   0,
      'Email':       0,
    };
    visitorsAll.forEach(v => {
      interactionCounts['Nav Bar CTA'] += Number(v.nav_cta_clicks || 0);
      interactionCounts['Hero Plays']  += Number(v.hero_plays     || 0);
      interactionCounts['Hero Pauses'] += Number(v.hero_pauses    || 0);
      interactionCounts['Hero Mute']   += Number(v.hero_mute      || 0);
      interactionCounts['Email']       += Number(v.footer_contact || 0);
    });
    const interactions = Object.entries(interactionCounts)
      .map(([element, clicks]) => ({ element, clicks }))
      .sort((a, b) => b.clicks - a.clicks);

    const workCardCounts: Record<string, number> = {};
    visitorsAll.forEach(v => {
      if (!v.work_cards) return;
      parseList(v.work_cards).forEach(card => {
        if (card) workCardCounts[card] = (workCardCounts[card] || 0) + 1;
      });
    });
    if (Object.keys(workCardCounts).length === 0) {
      leadsAll.forEach(lead => {
        if (lead.work_clicked) {
          parseList(lead.work_clicked).forEach(card => {
            if (card) workCardCounts[card] = (workCardCounts[card] || 0) + 1;
          });
        }
      });
    }
    const workCards = Object.entries(workCardCounts)
      .map(([name, clicks]) => ({ name, clicks }))
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 5);

    const brandCounts: Record<string, number> = {};
    visitorsAll.forEach(v => {
      if (!v.footer_sub_brands) return;
      parseList(v.footer_sub_brands).forEach(b => {
        if (b) brandCounts[b] = (brandCounts[b] || 0) + 1;
      });
    });
    if (Object.keys(brandCounts).length === 0) {
      leadsAll.forEach(lead => {
        if (lead.brand) brandCounts[lead.brand] = (brandCounts[lead.brand] || 0) + 1;
      });
    }
    const brandColors = ['#f4524d', '#c8f135', '#8b72d8', '#7a9bc4', '#f87171'];
    const brands = Object.entries(brandCounts)
      .map(([name, value], i) => ({
        name,
        value,
        color: brandColors[i % brandColors.length],
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    const socialByDayMap: Record<
      string,
      { instagram: number; youtube: number; linkedin: number; behance: number }
    > = {};
    visitorsAll.forEach(v => {
      const day = dateKey(v.created_at);
      if (!day) return;
      if (!socialByDayMap[day]) {
        socialByDayMap[day] = { instagram: 0, youtube: 0, linkedin: 0, behance: 0 };
      }
      if (!v.footer_socials) return;
      const tokens = parseList(v.footer_socials).map(t => t.toLowerCase());
      const joined = tokens.join(' ');
      if (joined.includes('instagram')) socialByDayMap[day].instagram++;
      if (joined.includes('youtube'))   socialByDayMap[day].youtube++;
      if (joined.includes('linkedin'))  socialByDayMap[day].linkedin++;
      if (joined.includes('behance'))   socialByDayMap[day].behance++;
    });
    const socialByDay = Object.entries(socialByDayMap)
      .map(([date, counts]) => ({ date, ...counts }))
      .sort((a, b) => a.date.localeCompare(b.date));

    const recentLeads = leadsAll.slice(0, 5);
    return { interactions, workCards, brands, socialByDay, recentLeads };
  }

  async exportLeads(
    format: 'csv' | 'json' = 'csv',
    filters?: { startDate?: string; endDate?: string },
  ): Promise<Blob> {
    const params = new URLSearchParams({ format });
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate)   params.append('endDate',   filters.endDate);
    const response = await fetch(`${API_BASE_URL}/export-leads.php?${params.toString()}`, {
      headers: { Authorization: `Bearer ${getSessionToken()}` },
    });
    if (!response.ok) throw new Error('Failed to export leads');
    return response.blob();
  }

  async exportVisitors(
    format: 'csv' | 'json' = 'csv',
    filters?: { startDate?: string; endDate?: string },
  ): Promise<Blob> {
    const params = new URLSearchParams({ format });
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate)   params.append('endDate',   filters.endDate);
    const response = await fetch(`${API_BASE_URL}/export-visitors.php?${params.toString()}`, {
      headers: { Authorization: `Bearer ${getSessionToken()}` },
    });
    if (!response.ok) throw new Error('Failed to export visitors');
    return response.blob();
  }

  // ── Deletes ─────────────────────────────────────────────────
  async deleteLead(id: number): Promise<void> {
    const res = await this.fetch(`/leads.php?id=${id}`, { method: 'DELETE' });
    if (res.status !== 'success') throw new Error(res.message || 'Failed to delete lead');
  }

  async clearLeadFlag(id: number): Promise<void> {
    const res = await this.fetch('/leads.php/clear-flag', {
      method: 'POST',
      body: JSON.stringify({ id }),
    });
    if (res.status !== 'success') throw new Error(res.message || 'Failed to clear flag');
  }

  async deleteManualLead(id: number): Promise<void> {
    const res = await this.fetch(`/manual-leads.php?id=${id}`, { method: 'DELETE' });
    if (res.status !== 'success') throw new Error(res.message || 'Failed to delete lead');
  }
}

export const api = new ApiClient();